import React, { useState } from 'react';
import { FaLinkedin, FaWhatsapp, FaBars, FaTimes, FaYoutube } from 'react-icons/fa';
import { SiGmail } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = ({ isDarkMode, toggleDarkMode }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuToggle = () => setMenuOpen((open) => !open);
  const handleLinkClick = () => setMenuOpen(false);

  const navVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const menuVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: -20
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: -20,
      transition: {
        duration: 0.2
      }
    }
  };

  const navLinks = [
    { href: "#home", text: "HOME" },
    { href: "#about", text: "ABOUT ME" },
    { href: "#experience", text: "EXPERIENCE" },
    { href: "#skills", text: "SKILLS" },
    { href: "#projects", text: "PROJECTS" },
    { href: "#certificates", text: "CERTIFICATES" },
    { href: "#contact", text: "CONTACT" }
  ];

  return (
    <motion.nav
      variants={navVariants}
      initial="hidden"
      animate="visible"
      className="fixed top-0 left-0 w-full bg-white/70 backdrop-blur-xl border-b border-gray-200/50 z-50 shadow-lg dark:bg-[#0B1020]/90 dark:border-blue-500/20 glass-card"
      style={{ WebkitBackdropFilter: 'blur(16px)', backdropFilter: 'blur(16px)' }}
    >
      <div className="container mx-auto px-4 h-16 flex items-center justify-between relative">
        {/* Enhanced Logo/Branding with Animated Gradient - Responsive */}
        <motion.div
          className="font-bold tracking-tight text-gray-900 dark:text-white animated-gradient-text text-sm sm:text-base md:text-lg lg:text-xl truncate max-w-[120px] sm:max-w-[180px] md:max-w-none"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <span className="hidden sm:inline">SANTHOSH KUMAR S</span>
          <span className="sm:hidden">SK</span>
        </motion.div>
        {/* Enhanced Desktop Navigation Links */}
        <div className="hidden md:flex gap-5 lg:gap-6 relative">
          {navLinks.map((link, index) => (
            <motion.a
              key={index}
              href={link.href}
              className="text-xs lg:text-sm text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 font-medium transition-all duration-300 relative group"
              whileHover={{ y: -2, color: '#22d3ee', textShadow: '0 0 16px #22d3ee66' }}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {link.text}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-400 transition-all duration-300 group-hover:w-full"></span>
            </motion.a>
          ))}
        </div>
        {/* Enhanced Social Icons and Menu */}
        <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
          {/* Enhanced Social Icons - Hidden on small screens */}
          <motion.a
            href="https://linkedin.com/in/santhosh-kumar-s-67465a239/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:block text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition-all duration-300 magnetic hover-glow"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaLinkedin className="text-xl" />
          </motion.a>
          <motion.a
            href="https://wa.me/919344705641"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:block text-gray-600 hover:text-green-600 dark:text-gray-300 dark:hover:text-green-400 transition-all duration-300 magnetic hover-glow"
            whileHover={{ scale: 1.1, rotate: -5 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaWhatsapp className="text-xl" />
          </motion.a>
          <motion.a
            href="mailto:santhoshkumar.btech1@gmail.com"
            className="hidden md:block text-gray-600 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400 transition-all duration-300 magnetic hover-glow"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
          >
            <SiGmail className="text-xl" />
          </motion.a>
          <motion.a
            href="https://www.youtube.com/@errorhumanity"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:block text-gray-600 hover:text-red-500 dark:text-gray-300 dark:hover:text-red-500 transition-all duration-300 magnetic hover-glow"
            whileHover={{ scale: 1.1, rotate: -5 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaYoutube className="text-xl" />
          </motion.a>
          {/* Enhanced Mobile Menu Button */}
          <motion.button
            onClick={handleMenuToggle}
            className="md:hidden p-2 rounded-lg bg-gray-100 dark:bg-[#121A2B] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#18243b] transition-all duration-300 magnetic hover-glow"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <AnimatePresence mode="wait">
              {menuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaTimes className="text-xl" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaBars className="text-xl" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>
      {/* Enhanced Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 bg-[#0B1020]/70 backdrop-blur-sm z-50"
            onClick={handleMenuToggle}
          >
            <motion.div
              variants={menuVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute top-16 left-0 w-full bg-white/95 dark:bg-[#0B1020]/95 backdrop-blur-md shadow-xl flex flex-col items-center py-8 space-y-6"
              onClick={e => e.stopPropagation()}
            >
              {navLinks.map((link, index) => (
                <motion.a
                  key={index}
                  href={link.href}
                  onClick={handleLinkClick}
                  className="text-gray-800 dark:text-white text-lg font-semibold hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300 relative group"
                  whileHover={{ x: 10 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.text}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-400 transition-all duration-300 group-hover:w-full"></span>
                </motion.a>
              ))}
              {/* Mobile Social Icons */}
              <motion.div
                className="flex space-x-6 pt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <motion.a
                  href="https://linkedin.com/in/santhosh-kumar-s-67465a239/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition-all duration-300 hover-glow"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaLinkedin className="text-2xl" />
                </motion.a>
                <motion.a
                  href="https://wa.me/919344705641"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-green-600 dark:text-gray-300 dark:hover:text-green-400 transition-all duration-300 hover-glow"
                  whileHover={{ scale: 1.2, rotate: -5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaWhatsapp className="text-2xl" />
                </motion.a>
                <motion.a
                  href="mailto:santhoshkumar.btech1@gmail.com"
                  className="text-gray-600 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400 transition-all duration-300 hover-glow"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SiGmail className="text-2xl" />
                </motion.a>
                <motion.a
                  href="https://www.youtube.com/@errorhumanity"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-red-500 dark:text-gray-300 dark:hover:text-red-500 transition-all duration-300 hover-glow"
                  whileHover={{ scale: 1.2, rotate: -5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaYoutube className="text-2xl" />
                </motion.a>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;